import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import * as routeRegistry
  from '@/governor/integration/AXRouteAuthorityRegistry';
import * as navigationAuthority
  from '@/governor/integration/AXNavigationAuthorityEngine';
import * as pageSeparation
  from '@/governor/integration/AXPageSeparationEngine';
import * as runtimeVerification
  from '@/governor/integration/AXRuntimeVerificationEngine';
import * as commandCenterFeed
  from '@/governor/integration/AXCommandCenterFeed';
import * as routerContract
  from '@/governor/integration/AXRouterIntegrationContract';
import * as runtimeDecision
  from '@/governor/integration/AXRuntimeDecisionEngine';
import * as runtimeAction
  from '@/governor/integration/AXRuntimeActionEngine';
import * as runtimeActionFeed
  from '@/governor/integration/AXRuntimeActionFeed';
import * as runtimeGuard
  from '@/governor/integration/AXRuntimeGuardHook';
import * as operationalCertification
  from '@/governor/integration/AXOperationalCertificationEngine';
import * as routeAuthority
  from '@/governor/routing/RouteAuthorityGovernor';

const STORAGE_KEY =
  'kinscircle_route_authority_runtime';

const EVENT_NAME =
  'kinscircle:route-authority-runtime';

type RuntimeResult = {
  readonly engine: string;
  readonly status: 'success' | 'failure' | 'unavailable';
  readonly output?: unknown;
  readonly error?: string;
};

const execute = (
  engine: string,
  candidate: unknown,
  pathname: string,
): RuntimeResult => {
  if (typeof candidate !== 'function') {
    return {
      engine,
      status: 'unavailable',
      error: 'Expected exported function is unavailable.',
    };
  }

  try {
    return {
      engine,
      status: 'success',
      output: (candidate as (path: string) => unknown)(
        pathname,
      ),
    };
  } catch (error) {
    return {
      engine,
      status: 'failure',
      error:
        error instanceof Error
          ? error.message
          : String(error),
    };
  }
};

const RouteAuthorityRuntimeBridge = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname =
      location.pathname || '/';

    const results: RuntimeResult[] = [
      execute(
        'AXRouteAuthorityRegistry',
        routeRegistry.getAXRouteAuthorityRegistry,
        pathname,
      ),
      execute(
        'AXNavigationAuthorityEngine',
        navigationAuthority.getAXNavigationAuthority,
        pathname,
      ),
      execute(
        'AXPageSeparationEngine',
        pageSeparation.getAXPageIdentities,
        pathname,
      ),
      execute(
        'AXRuntimeVerificationEngine',
        runtimeVerification.verifyAXRuntime,
        pathname,
      ),
      execute(
        'AXCommandCenterFeed',
        commandCenterFeed.getAXCommandCenterFeed,
        pathname,
      ),
      execute(
        'AXRouterIntegrationContract',
        routerContract.getAXRouterIntegrationContract,
        pathname,
      ),
      execute(
        'AXRuntimeDecisionEngine',
        runtimeDecision.evaluateAXRuntimeDecision,
        pathname,
      ),
      execute(
        'AXRuntimeActionEngine',
        runtimeAction.determineAXRuntimeAction,
        pathname,
      ),
      execute(
        'AXRuntimeActionFeed',
        runtimeActionFeed.getAXRuntimeActionFeed,
        pathname,
      ),
      execute(
        'AXRuntimeGuardHook',
        runtimeGuard.executeAXRuntimeGuard,
        pathname,
      ),
      execute(
        'AXOperationalCertificationEngine',
        operationalCertification.certifyAXGovernor,
        pathname,
      ),
      execute(
        'RouteAuthorityGovernor',
        routeAuthority.resolveRouteAuthority,
        pathname,
      ),
    ];

    if (
      pathname === '/' ||
      pathname === '/app' ||
      pathname === '/dashboard'
    ) {
      results.push(
        execute(
          'AppLayoutAuthority',
          routeAuthority.assertAppLayoutAuthority,
          pathname,
        ),
      );
    }

    const successful =
      results.filter(
        (result) => result.status === 'success',
      ).length;

    const failed =
      results.filter(
        (result) => result.status === 'failure',
      ).length;

    const unavailable =
      results.filter(
        (result) => result.status === 'unavailable',
      ).length;

    const detail = Object.freeze({
      domain: 'routing',
      governor: 'ax-route-authority-governor',
      runtime: true,
      stage: 'ROUTE_AUTHORITY_RUNTIME_EVALUATED',
      status:
        failed === 0 && unavailable === 0
          ? 'success'
          : failed > 0
            ? 'failure'
            : 'waiting',
      pathname,
      successful,
      failed,
      unavailable,
      results,
      timestamp: new Date().toISOString(),
    });

    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(detail),
      );
    } catch {
      // Runtime authority must continue even if storage fails.
    }

    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail,
      }),
    );

    window.dispatchEvent(
      new CustomEvent(
        'kinscircle:governor-runtime-event',
        { detail },
      ),
    );
  }, [location.pathname]);

  return null;
};

export default RouteAuthorityRuntimeBridge;
